import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Grid,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
} from '@mui/material';


import { useNavigate } from 'react-router-dom';
import { IMAGES } from '../../utils/constants';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';
import { useUserResetPassword } from '../../hooks/api/auth';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getURLParams } from '../../utils/data-model';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  password: ValidateField;
  confirmPassword: ValidateField;
};

const ResetPassword: FunctionComponent = () => {
  const navigate = useNavigate();
  const qparams: any = getURLParams()
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();

  const formValidation: ValidateFieldsState = {
    password: { invalid: false, error: '' },
    confirmPassword: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const { mutateAsync: resetPassword } = useUserResetPassword();

  useEffect(() => {
    if (!qparams?.token) {
      navigate('/login');
    }
  }, [qparams, navigate]);

  const isValidPassword = (password: string) => {
    // Check length
    if (password.length < 8) {
      return false;
    }
    
    // Check for at least one digit
    if (!/\d/.test(password)) {
      return false;
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      return false;
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      return false;
    }
    
    // Check for at least one special character
    if (!/[!@#$%^&*()_+\-=\[\]{}|\\:;"'<>,.?/]/.test(password)) {
      return false;
    }
    
    return true;
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!password.trim()) {
      errors.password = {
        invalid: true,
        error: 'Password is required',
      };
      valid = false;
    } else if (!isValidPassword(password)) {
      errors.password = {
        invalid: true,
        error: 'Password must meet all requirements.',
      };
      valid = false;
    } else {
      errors.password = { invalid: false, error: '' };
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = {
        invalid: true,
        error: 'Confirm Password is required',
      };
      valid = false;
    } else if (confirmPassword.trim() !== password.trim()) {
      errors.confirmPassword = {
        invalid: true,
        error: 'Confirm Passwords do not match',
      };
      valid = false;
    } else {
      errors.confirmPassword = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setValidateFields(formValidation);
    
    
    if (!validateForm()) {
      return;
    }
    
    try {
      
      const result = await resetPassword({
        token: qparams?.token,
        password: password,
      });
      if (result?.status === 200) {
        setSuccess(true)
        setTimeout(() => {
          setSuccess(false);
          navigate('/login');
        }, 3000);
      } else {
        setError('An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
          navigate('/forgot-password');
        }, 3000);
      }
    } catch (err: any) {
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#c3183c',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          width: 1000,
          height: 530,
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: '50%',
            backgroundImage: `linear-gradient(rgba(255, 0, 64, 0.4), rgba(255, 0, 64, 0.4)), url(${IMAGES.bannerImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box
          sx={{
            width: '50%',
            bgcolor: '#f9f9f9',
            px: 6,
            py: 5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4 }}
          >
            <Grid>
              <img
                src={IMAGES.barksdaleLogo}
                alt="Barksdale Logo"
                style={{ height: 50 }}
              />
            </Grid>
            <Grid>
              <img
                src={IMAGES.extranetLogo}
                alt="Extranet Logo"
                style={{ height: 50 }}
              />
            </Grid>
          </Grid>

          {/* Password Requirements */}
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#666' }}>
              Password Requirements:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                • Minimum 8 characters
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                • At least one uppercase letter (A-Z)
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                • At least one lowercase letter (a-z)
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                • At least one digit (0-9)
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                • At least one special character: !@#$%^&*()_+-=[]{}|\:;"'&lt;,&gt;.?/
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                • No spaces allowed
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={validateFields.password.invalid}
            helperText={validateFields.password.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onMouseEnter={() => setShowPassword(true)}
                    onMouseLeave={() => setShowPassword(false)}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: password ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            variant="outlined"
            margin="normal"
            type={'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={validateFields.confirmPassword.invalid}
            helperText={validateFields.confirmPassword.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputLabelProps={{
              sx: {
                color: password ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {
            success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Password changed successfully!
              </Alert>
            )
          }

          <Box display="flex" gap={2} mt={2} mb={2}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: '#c3183c',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#a31534',
                },
              }}
              onClick={handleSubmit}
            >
              SUBMIT
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
